import { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Clock, Loader2, MapPinOff, RefreshCw } from 'lucide-react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function TripTracker({ shareId, onBack }) {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);

  const fetchTrip = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/trip/public/${shareId}`);
      setTrip(res.data.trip);
      setError(null);
    } catch (e) {
      if (e.response?.status === 404) setError('Viaggio non trovato');
      else setError('Errore nel caricamento');
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchTrip();
    const interval = setInterval(() => fetchTrip(), 30000);
    return () => clearInterval(interval);
  }, [shareId]);

  // Initialize map
  useEffect(() => {
    if (!trip || !mapRef.current || mapInstance.current) return;
    
    const locations = trip.locations || [];
    const lastLoc = locations[locations.length - 1];
    const center = lastLoc ? [lastLoc.lat, lastLoc.lng] : [41.9028, 12.4964]; // Default: Rome

    mapInstance.current = L.map(mapRef.current, {
      center,
      zoom: lastLoc ? 13 : 4,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstance.current);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [trip?.share_id]);

  // Update markers and path
  useEffect(() => {
    if (!mapInstance.current || !trip) return;

    const locations = trip.locations || [];
    if (locations.length === 0) return;

    const lastLoc = locations[locations.length - 1];
    const coords = locations.map(l => [l.lat, l.lng]);

    // Update or create marker
    const tealIcon = L.divIcon({
      html: `<div style="background:#0d9488;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
      className: '',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    if (markerRef.current) {
      markerRef.current.setLatLng([lastLoc.lat, lastLoc.lng]);
    } else {
      markerRef.current = L.marker([lastLoc.lat, lastLoc.lng], { icon: tealIcon })
        .addTo(mapInstance.current)
        .bindPopup(`<b>${trip.traveler_name}</b><br>Ultimo aggiornamento: ${formatTime(lastLoc.timestamp)}`);
    }

    // Update or create polyline (travel path)
    if (polylineRef.current) {
      polylineRef.current.setLatLngs(coords);
    } else if (coords.length > 1) {
      polylineRef.current = L.polyline(coords, {
        color: '#0d9488',
        weight: 3,
        opacity: 0.7,
        dashArray: '8,8'
      }).addTo(mapInstance.current);
    }

    mapInstance.current.setView([lastLoc.lat, lastLoc.lng], mapInstance.current.getZoom());

  }, [trip?.locations?.length]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTimeSince = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Adesso';
    if (mins < 60) return `${mins} min fa`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ore fa`;
    return `${Math.floor(hours / 24)} giorni fa`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-slate-400">Caricamento viaggio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <div className="text-center bg-white/5 border border-slate-800 rounded-2xl p-8 max-w-md">
          <MapPinOff className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{error}</h2>
          <p className="text-slate-400 mb-6">Il link potrebbe essere scaduto o non valido.</p>
          {onBack && (
            <button onClick={onBack} className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2 rounded-lg transition-all">
              Torna alla home
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!trip?.is_active) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <div className="text-center bg-white/5 border border-slate-800 rounded-2xl p-8 max-w-md">
          <MapPinOff className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Condivisione disattivata</h2>
          <p className="text-slate-400 mb-2">{trip.traveler_name} ha disattivato la condivisione del viaggio.</p>
          {onBack && (
            <button onClick={onBack} className="mt-4 bg-teal-600 hover:bg-teal-500 text-white px-6 py-2 rounded-lg transition-all">
              Torna alla home
            </button>
          )}
        </div>
      </div>
    );
  }

  const lastLocation = trip.locations?.[trip.locations.length - 1];

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="trip-tracker">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600/20 to-transparent border-b border-teal-500/10 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500/20 rounded-full flex items-center justify-center">
              <Navigation className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">{trip.trip_name}</h1>
              <p className="text-teal-400 text-sm">{trip.traveler_name} {trip.destination && `- ${trip.destination}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchTrip(true)} disabled={refreshing}
              className="bg-white/5 hover:bg-white/10 text-slate-400 p-2 rounded-lg transition-all disabled:opacity-50"
              data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {onBack && (
              <button onClick={onBack} className="text-slate-500 hover:text-white text-sm transition-all">
                Chiudi
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info bar */}
      {lastLocation && (
        <div className="bg-black/30 border-b border-slate-800 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
              <span className="text-teal-400 font-medium">In viaggio</span>
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              <Clock className="w-3.5 h-3.5" />
              <span>Ultimo aggiornamento: {getTimeSince(lastLocation.timestamp)}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              <MapPin className="w-3.5 h-3.5" />
              <span>{trip.locations.length} posizioni</span>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="relative" style={{ height: 'calc(100vh - 140px)' }}>
        {(!trip.locations || trip.locations.length === 0) ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0f1419]">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">In attesa della prima posizione</h3>
              <p className="text-slate-400 text-sm max-w-sm">
                {trip.traveler_name} ha attivato la condivisione ma non ha ancora inviato la sua posizione.
                La mappa si aggiornera automaticamente.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-teal-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Aggiornamento automatico ogni 30 secondi
              </div>
            </div>
          </div>
        ) : (
          <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0f1419]/90 backdrop-blur-sm border-t border-slate-800 px-4 py-2">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span className="text-slate-600 text-xs">Powered by eSIM4You</span>
          <span className="text-slate-600 text-xs">Aggiornamento automatico ogni 30s</span>
        </div>
      </div>
    </div>
  );
}
